import * as React from 'react';
import { useEffect } from 'react';
import { format } from 'date-fns/format';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import moment from 'moment';
import { type MonthCaptionProps, useDayPicker } from 'react-day-picker';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

interface DatePickerProps {
  id: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  disabledBefore?: Date;
}
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const buildYears = (minYear: number, maxYear: number): number[] => {
  const list: number[] = [];
  for (let y = maxYear; y >= minYear; y--) list.push(y);
  return list;
};

const toDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.toDate() : undefined;
};

const toDateString = (date: Date) => moment(date).format('YYYY-MM-DD');
interface MonthYearCaptionProps extends MonthCaptionProps {
  disabledBefore?: Date;
}

const MonthYearCaption: React.FC<MonthYearCaptionProps> = ({
  calendarMonth,
  disabledBefore,
}) => {
  const { goToMonth } = useDayPicker();

  const currentYear = new Date().getFullYear();
  const minYear = disabledBefore
    ? disabledBefore.getFullYear()
    : currentYear - 100;
  const maxYear = currentYear + 10;
  const years = buildYears(minYear, maxYear);

  const activeMonth = calendarMonth.date.getMonth();
  const activeYear = calendarMonth.date.getFullYear();

  const [openPanel, setOpenPanel] = React.useState<'month' | 'year' | null>(
    null
  );

  const monthRef = React.useRef<HTMLDivElement>(null);
  const yearRef = React.useRef<HTMLDivElement>(null);

  const activeMonthItemRef = React.useRef<HTMLButtonElement>(null);
  const activeYearItemRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!openPanel) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideMonth = monthRef.current?.contains(target);
      const insideYear = yearRef.current?.contains(target);
      if (!insideMonth && !insideYear) setOpenPanel(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPanel]);

  useEffect(() => {
    if (openPanel === 'month') {
      activeMonthItemRef.current?.scrollIntoView({ block: 'center' });
    } else if (openPanel === 'year') {
      activeYearItemRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [openPanel]);

  const togglePanel = (panel: 'month' | 'year') =>
    setOpenPanel(prev => (prev === panel ? null : panel));

  const selectMonth = (idx: number) => {
    goToMonth(new Date(activeYear, idx, 1));
    setOpenPanel(null);
  };

  const selectYear = (year: number) => {
    goToMonth(new Date(year, activeMonth, 1));
    setOpenPanel(null);
  };

  const triggerCls =
    'flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-semibold ' +
    'hover:bg-accent transition-colors select-none cursor-pointer';

  const listCls =
    'absolute top-full mt-1 z-[60] min-w-[5rem] rounded-md border ' +
    'bg-popover shadow-lg overflow-y-auto max-h-52';

  const itemCls = (active: boolean) =>
    cn(
      'w-full text-left px-3 py-1.5 text-sm cursor-pointer transition-colors',
      active
        ? 'bg-primary text-primary-foreground font-semibold'
        : 'hover:bg-accent'
    );

  return (
    <div className="flex items-center justify-center gap-1">
      <div ref={monthRef} className="relative">
        <button
          type="button"
          className={triggerCls}
          onClick={() => togglePanel('month')}
          aria-label="Select month"
          aria-expanded={openPanel === 'month'}
        >
          {MONTHS[activeMonth]}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-150',
              openPanel === 'month' && 'rotate-180'
            )}
          />
        </button>

        {openPanel === 'month' && (
          <div className={listCls} style={{ left: 0 }}>
            {MONTHS.map((name, idx) => (
              <button
                key={name}
                type="button"
                ref={idx === activeMonth ? activeMonthItemRef : undefined}
                className={itemCls(idx === activeMonth)}
                onClick={() => selectMonth(idx)}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div ref={yearRef} className="relative">
        <button
          type="button"
          className={triggerCls}
          onClick={() => togglePanel('year')}
          aria-label="Select year"
          aria-expanded={openPanel === 'year'}
        >
          {activeYear}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-150',
              openPanel === 'year' && 'rotate-180'
            )}
          />
        </button>

        {openPanel === 'year' && (
          <div className={listCls} style={{ left: 0 }}>
            {years.map(y => (
              <button
                key={y}
                type="button"
                ref={y === activeYear ? activeYearItemRef : undefined}
                className={itemCls(y === activeYear)}
                onClick={() => selectYear(y)}
              >
                {y}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
const DatePicker: React.FC<DatePickerProps> = ({
  id,
  value,
  onChange,
  placeholder = 'Pick a date',
  className,
  hasError,
  disabledBefore,
}) => {
  const [open, setOpen] = React.useState(false);
  const selectedDate = toDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            'w-full justify-start gap-2 font-normal',
            !selectedDate && 'text-muted-foreground',
            hasError && 'border-red-500',
            className
          )}
        >
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          {selectedDate ? (
            format(selectedDate, 'PPP')
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0 overflow-visible"
        align="start"
        portalled={false}
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={selectedDate ?? new Date()}
          onSelect={date => {
            if (!date) return;
            onChange(toDateString(date));
            setOpen(false);
          }}
          disabled={disabledBefore ? { before: disabledBefore } : undefined}
          components={{
            MonthCaption: (props: MonthCaptionProps) => (
              <MonthYearCaption {...props} disabledBefore={disabledBefore} />
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
